import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { requestNotificationPermission, showNotification } from '@/lib/notifications';

/** Check if notification sound is globally muted */
export function isNotificationMuted(): boolean {
  return localStorage.getItem('msg-sound-muted') === 'true';
}

/** Toggle global notification sound mute state */
export function setNotificationMuted(muted: boolean) {
  localStorage.setItem('msg-sound-muted', muted ? 'true' : 'false');
}

/** Check if a specific room is muted */
export function isRoomMuted(roomId: string): boolean {
  const muted = JSON.parse(localStorage.getItem('msg-muted-rooms') || '[]');
  return muted.includes(roomId);
}

/** Toggle mute for a specific room */
export function toggleRoomMuted(roomId: string): boolean {
  const muted: string[] = JSON.parse(localStorage.getItem('msg-muted-rooms') || '[]');
  const isMuted = muted.includes(roomId);
  const next = isMuted ? muted.filter(id => id !== roomId) : [...muted, roomId];
  localStorage.setItem('msg-muted-rooms', JSON.stringify(next));
  return !isMuted;
}

/** Shared AudioContext singleton – unlocked on first user gesture (required by Safari/iOS) */
let sharedAudioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!sharedAudioCtx) {
    sharedAudioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return sharedAudioCtx;
}

/** Resume the AudioContext on first user interaction (iOS/Safari requirement) */
function unlockAudio() {
  const ctx = getAudioContext();
  if (ctx.state === 'suspended') {
    ctx.resume();
  }
  // Play a silent buffer to fully unlock on iOS
  const buffer = ctx.createBuffer(1, 1, 22050);
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.connect(ctx.destination);
  source.start(0);
  // Remove listeners after unlock
  window.removeEventListener('touchstart', unlockAudio, true);
  window.removeEventListener('touchend', unlockAudio, true);
  window.removeEventListener('click', unlockAudio, true);
}

// Register unlock listeners immediately
if (typeof window !== 'undefined') {
  window.addEventListener('touchstart', unlockAudio, true);
  window.addEventListener('touchend', unlockAudio, true);
  window.addEventListener('click', unlockAudio, true);
}

/** Play a short notification "ding" sound using Web Audio API */
function playNotificationSound() {
  if (isNotificationMuted()) return;
  try {
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.4);
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(1174.66, ctx.currentTime + 0.15);
    gain2.gain.setValueAtTime(0.2, ctx.currentTime + 0.15);
    gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    osc2.start(ctx.currentTime + 0.15);
    osc2.stop(ctx.currentTime + 0.5);
  } catch {
    // Audio not available
  }
}

/**
 * Hook to track unread message counts per room and total.
 * Uses chat_read_receipts table to determine last read timestamp.
 */
export function useUnreadCounts() {
  const { user } = useAuth();
  const [unreadByRoom, setUnreadByRoom] = useState<Record<string, number>>({});
  const [totalUnread, setTotalUnread] = useState(0);

  const fetchUnreadCounts = useCallback(async () => {
    if (!user) return;

    // Get all rooms the user is a member of
    const { data: memberships } = await supabase
      .from('chat_room_members')
      .select('room_id')
      .eq('user_id', user.id);

    if (!memberships || memberships.length === 0) {
      setUnreadByRoom({});
      setTotalUnread(0);
      return;
    }

    const roomIds = memberships.map(m => m.room_id);

    // Get read receipts for all rooms
    const { data: receipts } = await supabase
      .from('chat_read_receipts')
      .select('room_id, last_read_at')
      .eq('user_id', user.id)
      .in('room_id', roomIds);

    const receiptMap: Record<string, string> = {};
    (receipts || []).forEach(r => {
      receiptMap[r.room_id] = r.last_read_at;
    });

    // Count unread messages per room
    const counts: Record<string, number> = {};
    let total = 0;

    for (const roomId of roomIds) {
      const lastRead = receiptMap[roomId];
      let query = supabase
        .from('chat_messages')
        .select('id', { count: 'exact', head: true })
        .eq('room_id', roomId)
        .neq('sender_id', user.id);

      if (lastRead) {
        query = query.gt('created_at', lastRead);
      }

      const { count } = await query;
      const c = count || 0;
      if (c > 0) {
        counts[roomId] = c;
        total += c;
      }
    }

    setUnreadByRoom(counts);
    setTotalUnread(total);
  }, [user]);

  // Mark a room as read
  const markAsRead = useCallback(async (roomId: string) => {
    if (!user) return;

    const { data: existing } = await supabase
      .from('chat_read_receipts')
      .select('id')
      .eq('room_id', roomId)
      .eq('user_id', user.id)
      .maybeSingle();

    if (existing) {
      await supabase
        .from('chat_read_receipts')
        .update({ last_read_at: new Date().toISOString() })
        .eq('id', existing.id);
    } else {
      await supabase
        .from('chat_read_receipts')
        .insert({ room_id: roomId, user_id: user.id, last_read_at: new Date().toISOString() });
    }

    setUnreadByRoom(prev => {
      const next = { ...prev };
      delete next[roomId];
      return next;
    });
    setTotalUnread(prev => prev - (unreadByRoom[roomId] || 0));
  }, [user, unreadByRoom]);

  // Initial fetch + request ALL permissions upfront
  useEffect(() => {
    fetchUnreadCounts();
    if (user) {
      // Request notification permission immediately
      requestNotificationPermission();
      // Request microphone permission for voice notes
      if (navigator.mediaDevices?.getUserMedia) {
        navigator.mediaDevices.getUserMedia({ audio: true })
          .then(stream => stream.getTracks().forEach(t => t.stop()))
          .catch(() => {});
      }
    }
  }, [fetchUnreadCounts, user]);

  // Listen for new messages in realtime with auto-reconnect
  useEffect(() => {
    if (!user) return;

    let channel: ReturnType<typeof supabase.channel> | null = null;
    let retryTimeout: ReturnType<typeof setTimeout> | null = null;
    let retryCount = 0;
    let disposed = false;

    const setupChannel = () => {
      if (disposed) return;
      
      // Clean up previous channel
      if (channel) {
        supabase.removeChannel(channel);
      }

      console.log('[UnreadCounts] Setting up realtime subscription for user', user.id, 'attempt', retryCount + 1);

      channel = supabase
        .channel('unread-counts-' + user.id + '-' + Date.now())
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'chat_messages' },
          (payload) => {
            const msg = payload.new as any;
            console.log('[UnreadCounts] Realtime INSERT received:', msg.id);
            if (msg.sender_id !== user.id) {
              setUnreadByRoom(prev => ({
                ...prev,
                [msg.room_id]: (prev[msg.room_id] || 0) + 1,
              }));
              setTotalUnread(prev => prev + 1);

              if (!isRoomMuted(msg.room_id)) {
                playNotificationSound();
                showNotification(
                  'Logi - رسالة جديدة',
                  msg.content?.substring(0, 100) || 'لديك رسالة جديدة',
                  { tag: 'msg-' + msg.room_id }
                );
              }
            }
          }
        )
        .subscribe((status) => {
          console.log('[UnreadCounts] Realtime subscription status:', status);
          if (status === 'SUBSCRIBED') {
            retryCount = 0; // Reset on success
          } else if (status === 'TIMED_OUT' || status === 'CLOSED' || status === 'CHANNEL_ERROR') {
            // Auto-reconnect with exponential backoff
            const delay = Math.min(2000 * Math.pow(2, retryCount), 30000);
            retryCount++;
            console.log('[UnreadCounts] Will retry in', delay, 'ms');
            retryTimeout = setTimeout(setupChannel, delay);
          }
        });
    };

    setupChannel();

    // Also poll as fallback every 30s
    const pollInterval = setInterval(() => {
      fetchUnreadCounts();
    }, 30000);

    return () => {
      disposed = true;
      if (retryTimeout) clearTimeout(retryTimeout);
      clearInterval(pollInterval);
      if (channel) supabase.removeChannel(channel);
    };
  }, [user, fetchUnreadCounts]);

  return { unreadByRoom, totalUnread, markAsRead, refetch: fetchUnreadCounts };
}
